/* eslint-disable no-undef */
/* global BigInt */
import React, { useState, useEffect, useCallback, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { BrowserProvider, Contract } from "@coti-io/coti-ethers";
import { Loader2, CheckCircle2, Lock, ChevronDown, Gamepad2, Volume2 } from "lucide-react";

const COTI_CHAIN_ID = 2632500;
const CONTRACT_ADDRESS = "0xEa2A6568da588E1aD2566726005dD0Ef714324e9";
const BRM_ABI = [
  "function mint(address account, uint64 amount) nonpayable",
  "function balanceOf(address account) view returns (uint256)"
];

// --- FUNGSI SUARA GLOBAL ---
const playSound = (type) => {
  const AudioContext = window.AudioContext || window.webkitAudioContext;
  if (!AudioContext) return;
  const ctx = new AudioContext();
  const osc = ctx.createOscillator();
  const gain = ctx.createGain();
  osc.connect(gain); gain.connect(ctx.destination);

  if (type === 'correct' || type === 'snake_eat') {
    osc.type = 'sine'; osc.frequency.setValueAtTime(800, ctx.currentTime);
    osc.frequency.exponentialRampToValueAtTime(1200, ctx.currentTime + 0.1);
    gain.gain.setValueAtTime(0.1, ctx.currentTime); osc.start(); osc.stop(ctx.currentTime + 0.1);
  } else if (type === 'wrong' || type === 'game_over') {
    osc.type = 'sawtooth'; osc.frequency.setValueAtTime(200, ctx.currentTime);
    gain.gain.setValueAtTime(0.1, ctx.currentTime); osc.start(); osc.stop(ctx.currentTime + 0.3);
  } else if (type === 'win') {
    osc.type = 'triangle'; osc.frequency.setValueAtTime(400, ctx.currentTime);
    osc.frequency.exponentialRampToValueAtTime(800, ctx.currentTime + 0.5);
    gain.gain.setValueAtTime(0.1, ctx.currentTime); osc.start(); osc.stop(ctx.currentTime + 0.5);
  }
};

const TokenBalance = ({ signer, address, hasAesKey, refreshTrigger }) => {
  const [balance, setBalance] = useState(null);
  const [loading, setLoading] = useState(false);
  const fetchBalance = useCallback(async () => {
    if (!signer || !address || !hasAesKey) return;
    setLoading(true);
    try {
      const contract = new Contract(CONTRACT_ADDRESS, BRM_ABI, signer);
      const encBalance = await contract.balanceOf(address);
      const decBalance = await signer.decryptValue(encBalance);
      setBalance(Number(decBalance) / 10**6);
    } catch (e) { console.error(e); }
    setLoading(false);
  }, [signer, address, hasAesKey]);
  useEffect(() => { fetchBalance(); }, [fetchBalance, refreshTrigger]);
  if (!address) return null;
  return (
    <div className="px-3 py-1.5 bg-green-50 rounded-lg text-sm font-medium text-green-700 flex items-center gap-1 border border-green-100">
      {hasAesKey ? (loading ? "💰 ..." : `💰 ${balance?.toLocaleString(undefined,{minimumFractionDigits:2})} BRM`) : <><Lock size={12}/> --- BRM</>}
    </div>
  );
};

const ClaimPrizeButton = ({ amount, userAddress, signer, onClaimed }) => {
  const [state, setState] = useState("idle");
  const handleClaim = async () => {
    setState("loading");
    try {
      const brmContract = new Contract(CONTRACT_ADDRESS, BRM_ABI, signer);
      const val = BigInt(Math.floor(amount * 10**6));
      const tx = await brmContract.mint(userAddress, val, { gasLimit: 12000000 });
      await tx.wait();
      setState("success"); if (onClaimed) onClaimed();
    } catch (err) { alert("Error: " + err.message); setState("idle"); }
  };
  return (
    <div className="mt-4 flex flex-col items-center">
      {state === "idle" && <button onClick={handleClaim} className="bg-green-600 text-white px-8 py-3 rounded-xl font-bold">Claim {amount} BRM</button>}
      {state === "loading" && <div className="animate-spin"><Loader2/></div>}
      {state === "success" && <div className="text-green-600 font-bold">Success!</div>}
    </div>
  );
};

const MemoryGame = ({ wallet, onWin }) => {
  const [st, setSt] = useState('idle'); const [cards, setCards] = useState([]); const [exp, setExp] = useState(1);
  const start = () => {
    const n = [1,2,3,4,5,6,7,8,9].sort(()=>Math.random()-0.5);
    setCards(n.map(v=>({id:v, val:v, f:true}))); setSt('rev'); setExp(1);
    setTimeout(()=>{ setCards(p=>p.map(c=>({...c, f:false}))); setSt('play'); }, 2500);
  };
  const click = (c) => {
    if (st!=='play'||c.f) return;
    if (c.val===exp) {
      playSound('correct');
      setCards(p=>p.map(x=>x.id===c.id?{...x, f:true}:x));
      if (exp===9) { playSound('win'); setSt('won'); } else setExp(e=>e+1);
    } else { playSound('wrong'); setSt('lost'); }
  };
  return (
    <div className="bg-white p-6 rounded-3xl shadow-xl border border-gray-100">
      <div className="grid grid-cols-3 gap-3 bg-gray-900 p-3 rounded-2xl mb-4">
        {cards.map(c=> <button key={c.id} onClick={() => click(c)} className={`h-16 rounded-xl font-bold text-xl ${c.f?'bg-white text-black':'bg-gray-700 text-transparent'}`}>{c.f?c.val:"?"}</button>)}
      </div>
      {st==='idle'&&<button onClick={start} className="w-full bg-blue-600 text-white py-4 rounded-2xl font-bold">Start Memory</button>}
      {st==='won'&&<ClaimPrizeButton amount={10} userAddress={wallet.address} signer={wallet.signer} onClaimed={onWin} />}
      {st==='lost'&&<button onClick={start} className="w-full bg-red-500 text-white py-4 rounded-2xl font-bold">Try Again</button>}
    </div>
  );
};

const ReactionGame = ({ wallet, onWin }) => {
  const [st, setSt] = useState('idle'); const [t, setT] = useState(0); const [s, setS] = useState(0);
  const run = () => { setSt('wait'); setTimeout(()=>{ setSt('go'); setS(Date.now()); }, Math.random()*2000 + 1000); };
  return (
    <div className="bg-white p-8 rounded-3xl shadow-xl border border-gray-100 text-center">
      <div onClick={()=>{ if(st==='go'){ playSound('correct'); setT(Date.now()-s); setSt('end'); } }} className={`h-40 rounded-2xl mb-4 flex items-center justify-center text-white font-bold text-3xl cursor-pointer ${st==='go'?'bg-blue-500':'bg-gray-800'}`}>
        {st==='idle'?"Ready?":st==='wait'?"Wait...":st==='go'?"CLICK!":t+"ms"}
      </div>
      {st==='idle'&&<button onClick={run} className="w-full bg-black text-white py-4 rounded-2xl font-bold">Start Test</button>}
      {st==='end'&&(<div>{t<400 ? <><div className="text-green-600 font-bold">Fast!</div><ClaimPrizeButton amount={5} userAddress={wallet.address} signer={wallet.signer} onClaimed={onWin} /></> : <div className="text-red-500">Too slow!</div>} <button onClick={run} className="block w-full mt-2 text-blue-600 font-bold">Retry</button></div>)}
    </div>
  );
};

const SnakeGame = ({ wallet, onWin }) => {
  const canvasRef = useRef(null); const [score, setScore] = useState(0); const [gameOver, setGameOver] = useState(false); const [gameStarted, setGameStarted] = useState(false);
  const GRID_SIZE = 20; const CANVAS_SIZE = 300;
  useEffect(() => {
    if (!gameStarted || gameOver) return;
    const canvas = canvasRef.current; const ctx = canvas.getContext('2d');
    let snake = [{x: 10, y: 10}]; let food = {x: 15, y: 15}; let dx = 1, dy = 0; let currentScore = 0;
    const handleKey = (e) => {
      if (e.key === 'ArrowUp' && dy === 0) { dx = 0; dy = -1; }
      else if (e.key === 'ArrowDown' && dy === 0) { dx = 0; dy = 1; }
      else if (e.key === 'ArrowLeft' && dx === 0) { dx = -1; dy = 0; }
      else if (e.key === 'ArrowRight' && dx === 0) { dx = 1; dy = 0; }
    };
    window.addEventListener('keydown', handleKey);
    const gameLoop = setInterval(() => {
      const head = {x: snake[0].x + dx, y: snake[0].y + dy};
      if (head.x < 0 || head.x >= CANVAS_SIZE/GRID_SIZE || head.y < 0 || head.y >= CANVAS_SIZE/GRID_SIZE || snake.some(s => s.x === head.x && s.y === head.y)) {
        playSound('game_over'); setGameOver(true); clearInterval(gameLoop); return;
      }
      snake.unshift(head);
      if (head.x === food.x && head.y === food.y) {
        playSound('snake_eat'); currentScore += 10; setScore(currentScore);
        food = { x: Math.floor(Math.random() * (CANVAS_SIZE/GRID_SIZE)), y: Math.floor(Math.random() * (CANVAS_SIZE/GRID_SIZE)) };
      } else { snake.pop(); }
      ctx.fillStyle = '#2c2e3a'; ctx.fillRect(0, 0, CANVAS_SIZE, CANVAS_SIZE);
      ctx.fillStyle = '#10B981'; snake.forEach(s => ctx.fillRect(s.x*GRID_SIZE, s.y*GRID_SIZE, GRID_SIZE-2, GRID_SIZE-2));
      ctx.fillStyle = '#EF4444'; ctx.fillRect(food.x*GRID_SIZE, food.y*GRID_SIZE, GRID_SIZE-2, GRID_SIZE-2);
    }, 150);
    return () => { clearInterval(gameLoop); window.removeEventListener('keydown', handleKey); };
  }, [gameStarted, gameOver]);
  return (
    <div className="bg-white p-6 rounded-3xl shadow-xl border border-gray-100 flex flex-col items-center">
      <div className="flex justify-between w-full mb-4 font-bold">
        <h3 className="text-xl">Snake Classic</h3>
        <div className="text-green-600">Score: {score}</div>
      </div>
      <canvas ref={canvasRef} width={CANVAS_SIZE} height={CANVAS_SIZE} className="bg-gray-900 rounded-xl mb-4 border-4 border-gray-800" />
      {!gameStarted && <button onClick={() => setGameStarted(true)} className="w-full bg-blue-600 text-white py-3 rounded-xl font-bold">Start Nokia Snake</button>}
      <div className="grid grid-cols-3 gap-2 w-full max-w-[150px] mb-4">
        <div /> <button onClick={() => window.dispatchEvent(new KeyboardEvent('keydown', {key: 'ArrowUp'}))} className="bg-gray-200 p-3 rounded-lg">▲</button> <div />
        <button onClick={() => window.dispatchEvent(new KeyboardEvent('keydown', {key: 'ArrowLeft'}))} className="bg-gray-200 p-3 rounded-lg">◀</button>
        <button onClick={() => window.dispatchEvent(new KeyboardEvent('keydown', {key: 'ArrowDown'}))} className="bg-gray-200 p-3 rounded-lg">▼</button>
        <button onClick={() => window.dispatchEvent(new KeyboardEvent('keydown', {key: 'ArrowRight'}))} className="bg-gray-200 p-3 rounded-lg">▶</button>
      </div>
      {gameOver && (
        <div className="text-center w-full">
          <p className="text-red-500 font-bold">Game Over!</p>
          {score >= 50 && <ClaimPrizeButton amount={5} userAddress={wallet.address} signer={wallet.signer} onClaimed={onWin} />}
          <button onClick={() => { setGameOver(false); setScore(0); setGameStarted(true); }} className="mt-2 text-blue-600 underline">Try Again</button>
        </div>
      )}
    </div>
  );
};

export default function App() {
  const [wallet, setWallet] = useState({ isConnected: false, address: null, signer: null });
  const [onboarded, setOnboarded] = useState(false);
  const [refresh, setRefresh] = useState(0);
  const [activeGame, setActiveGame] = useState('memory');
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);

  const connectWallet = async () => {
    if (!window.ethereum) return;
    const provider = new BrowserProvider(window.ethereum);
    await window.ethereum.request({ method: "eth_requestAccounts" });
    const signer = await provider.getSigner();
    const address = await signer.getAddress();
    setWallet({ isConnected: true, address, signer });
    const list = JSON.parse(localStorage.getItem('walletsList') || '[]');
    if (list.find(w => w.wallet === address)) setOnboarded(true);
  };

  const runOnboarding = async () => {
    if (!wallet.signer) return;
    try {
      await wallet.signer.generateOrRecoverAes();
      const list = JSON.parse(localStorage.getItem('walletsList') || '[]');
      if (!list.find(w => w.wallet === wallet.address)) {
        list.push({ wallet: wallet.address });
        localStorage.setItem('walletsList', JSON.stringify(list));
      }
      setOnboarded(true);
    } catch (e) { alert("Sign to onboard!"); }
  };

  const games = [
    { id: 'memory', name: 'Memory Order', icon: '🧠' },
    { id: 'reaction', name: 'Reaction Strike', icon: '⚡' },
    { id: 'snake', name: 'Classic Snake', icon: '🐍' }
  ];

  return (
    <div className="min-h-screen bg-[#F7F9FC] pb-20">
      <nav className="p-4 bg-white/80 backdrop-blur-md border-b flex justify-between items-center sticky top-0 z-50 px-4">
        <div className="font-bold text-xl text-blue-600">BrainMint</div>
        <div className="flex items-center gap-2">
          <TokenBalance signer={wallet.signer} address={wallet.address} hasAesKey={onboarded} refreshTrigger={refresh} />
          <div className="relative">
            <button onClick={() => setIsDropdownOpen(!isDropdownOpen)} className="bg-gray-100 p-2 rounded-lg"><Gamepad2 size={20}/></button>
            <AnimatePresence>
              {isDropdownOpen && (
                <motion.div initial={{opacity:0}} animate={{opacity:1}} className="absolute right-0 mt-2 w-40 bg-white border rounded-xl shadow-xl p-1 z-50">
                  {games.map(g => (
                    <button key={g.id} onClick={() => { setActiveGame(g.id); setIsDropdownOpen(false); }} className="w-full text-left p-2 hover:bg-gray-50 flex items-center gap-2 text-sm font-bold">
                      {g.icon} {g.name}
                    </button>
                  ))}
                </motion.div>
              )}
            </AnimatePresence>
          </div>
          <button onClick={connectWallet} className="bg-black text-white px-3 py-1.5 rounded-lg text-xs font-bold">
            {wallet.isConnected ? wallet.address.slice(0,4)+'...'+wallet.address.slice(-2) : 'Connect'}
          </button>
        </div>
      </nav>

      <main className="p-6 max-w-md mx-auto">
        {!wallet.isConnected ? (
          <button onClick={connectWallet} className="w-full bg-blue-600 text-white p-10 rounded-3xl font-bold shadow-xl">Connect Wallet to Play</button>
        ) : (
          <>
            <div className="text-center mb-6">
               <h1 className="text-3xl font-black">{games.find(g=>g.id===activeGame).name}</h1>
               {!onboarded && <button onClick={runOnboarding} className="bg-orange-500 text-white px-4 py-1 rounded-full text-[10px] font-bold mt-2 animate-pulse">TAP TO REVEAL BALANCE (FREE SIGN)</button>}
            </div>
            {activeGame === 'memory' && <MemoryGame wallet={wallet} onWin={() => setRefresh(r => r + 1)} />}
            {activeGame === 'reaction' && <ReactionGame wallet={wallet} onWin={() => setRefresh(r => r + 1)} />}
            {activeGame === 'snake' && <SnakeGame wallet={wallet} onWin={() => setRefresh(r => r + 1)} />}
          </>
        )}
      </main>
    </div>
  );
}
